import * as React from "react";
import { CalendarRange, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePeriod } from "@/context/PeriodContext";
import { PERIOD_PRESETS } from "@/lib/period";
import { cn } from "@/lib/cn";

export function PeriodFilter() {
  const { period, setPeriodKey, setCustomRange } = usePeriod();
  const [open, setOpen] = React.useState(false);
  const [customFrom, setCustomFrom] = React.useState(period.from);
  const [customTo, setCustomTo] = React.useState(period.to);

  React.useEffect(() => {
    if (period.key === "custom") {
      setCustomFrom(period.from);
      setCustomTo(period.to);
    }
  }, [period]);

  function applyCustom() {
    if (!customFrom || !customTo) return;
    setCustomRange(customFrom, customTo);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex h-9 items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 text-sm text-white/80 transition-colors hover:border-white/30 hover:text-white">
          <CalendarRange className="size-4 shrink-0" />
          <span className="hidden max-w-32 truncate sm:inline">{period.label}</span>
          <ChevronDown className="size-3.5 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Period Covered</p>
        <div className="flex flex-col gap-1">
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={() => {
                setPeriodKey(preset.key);
                setOpen(false);
              }}
              className={cn(
                "rounded-md px-2.5 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent",
                period.key === preset.key && "bg-brand-muted font-medium text-brand hover:bg-brand-muted",
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="mt-2 border-t border-border pt-2">
          <p className={cn("mb-1.5 px-1 text-sm text-foreground", period.key === "custom" && "font-medium text-brand")}>
            Custom Range
          </p>
          <div className="grid grid-cols-2 gap-2 px-1">
            <div className="flex flex-col gap-1">
              <Label htmlFor="period-from" className="text-[10px] text-muted-foreground">
                From
              </Label>
              <Input
                id="period-from"
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="period-to" className="text-[10px] text-muted-foreground">
                To
              </Label>
              <Input
                id="period-to"
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          </div>
          <Button size="sm" className="mt-2 w-full" onClick={applyCustom} disabled={!customFrom || !customTo}>
            Apply Custom Range
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
